import { Collapse, Form, Spin, Table } from 'antd';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useContext } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { AutoInventoryStepsModern } from 'app/typings/autoInventory';
import type { InventoryResponse } from 'app/typings/core/extranet/inventory/autoInventory';
import { format } from 'app/utils/dayjs';

import { ConfigStep } from '../ConfigStep/ConfigStep';

import styles from './Preview.module.scss';
import type { PreviewFormValues, RoomWithInventory } from './utils';
import { associateRooms, organizeInventoryData } from './utils';

type OrganizedData = ReturnType<typeof organizeInventoryData>;

type PreviewProps = {
  handleStepChange: (step: AutoInventoryStepsModern) => void;
  onDeactivate?: () => void;
  isDisabling?: boolean;
  isSaving?: boolean;
  previewInventory?: InventoryResponse;
  isLoading?: boolean;
};

const buildDateEntries = (
  dates: string[],
  fn: (i: number) => ReactNode
): Record<string, ReactNode> =>
  Object.fromEntries(dates.map((_, i) => [`date${i}`, fn(i)]));

export const Preview = ({
  handleStepChange,
  onDeactivate,
  isDisabling,
  isSaving,
  previewInventory,
  isLoading = false,
}: PreviewProps) => {
  const { currencyFormatter } = useContext(CurrencyContext);
  const form = Form.useFormInstance();
  const formData = Form.useWatch<PreviewFormValues>([], form);

  const filteredRooms = associateRooms(
    formData,
    previewInventory?.inventory ?? []
  );

  const defaultActiveKey =
    filteredRooms.length > 0 ? [filteredRooms[0].id.toString()] : [];

  const buildColumns = (dates: string[]) => [
    {
      title: <span className={styles.timePeriod}>Time period</span>,
      dataIndex: 'timePeriod',
      key: 'timePeriod',
    },
    ...dates.map((date, i) => ({
      title: (
        <div className={styles.dateHeader}>
          <span>{format(new Date(date), 'ddd')}</span>
          {format(new Date(date), 'DD/MM')}
        </div>
      ),
      dataIndex: `date${i}`,
      key: `date${i}`,
    })),
  ];

  const buildBaseRows = (organizedData: OrganizedData) => {
    return [
      {
        key: 'stock',
        timePeriod: <span>Remaining Stock</span>,
        ...buildDateEntries(organizedData.dates, (i) => (
          <span
            className={classNames({
              [styles.stockZero]: organizedData.stocks[i] === 0,
            })}
          >
            {organizedData.stocks[i]}
          </span>
        )),
      },
      {
        key: 'bar',
        timePeriod: (
          <span className={styles.labelWithIcon}>BAR RO (NA/NR)</span>
        ),
        ...buildDateEntries(organizedData.dates, (i) => (
          <span className={styles.barValue}>
            {currencyFormatter(Math.floor(organizedData.bars[i] ?? 0))}
          </span>
        )),
      },
    ];
  };

  return (
    <ConfigStep
      step={AutoInventoryStepsModern.preview}
      className={styles.preview}
      handleStepChange={handleStepChange}
      onDeactivate={onDeactivate}
      isSaving={isSaving}
      isDisabling={isDisabling}
    >
      <p className={styles.explanation}>
        Based on your configuration this is what we have identified:
      </p>

      <div className={styles.collapses}>
        <Spin spinning={isLoading}>
          <Collapse
            key={defaultActiveKey.length}
            defaultActiveKey={defaultActiveKey}
            className={styles.collapse}
          >
            {filteredRooms.map((room: RoomWithInventory) => {
              const organizedData = organizeInventoryData(
                room.inventory,
                formData
              );
              const columns = buildColumns(organizedData.dates);

              return (
                <Collapse.Panel header={room.name} key={room.id.toString()}>
                  <Table
                    className={styles.table}
                    dataSource={buildBaseRows(organizedData)}
                    columns={columns}
                    rowKey="key"
                    pagination={false}
                    rowClassName={(r) =>
                      styles[`${r.key}Row` as keyof typeof styles] ?? ''
                    }
                    bordered={false}
                  />
                </Collapse.Panel>
              );
            })}
          </Collapse>
        </Spin>
      </div>
    </ConfigStep>
  );
};
